import { GetServerSidePropsContext } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

import {
  PAGE_TYPE,
  Referrer,
} from '@invideoio/web-shared/utils/utils.constant';

import CategoryTemplateContent from '@components/category-templates/CategoryTemplateContent';
import CategoryTemplateHead from '@components/category-templates/CategoryTemplateHead';
import HomepageSEO from '@components/common/SEO/HomepageSEO';
import { footerExploreLinks } from '@constants/footerExplore';
import Footer from '@invideoio/web-shared/components/Footer';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import AppLayout from '@layouts/AppLayout';
import {
  fetchAllTemplateCategories,
  fetchTemplatePageData,
  searchTemplates,
} from '@lib/API/template.api';
import { DispatchContext, StateContext } from '@store/store';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import { parseFiltersFromQueryParams } from '@utils/template-category/filter.utils';

import CategoryTemplateLeftPanel from '@components/category-templates/CategoryTemplateLeftPanel';
import CategoryTemplates from '@components/category-templates/CategoryTemplates';
import HeaderWithCTA from '@components/header-with-cta';
import { TEMPLATE_LIMIT } from '@constants/templates';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { PageType } from '@invideoio/web-shared/types/Analytics';
import {
  TemplateSearchData,
  TemplateSearchPayload,
  TemplateSearchResponse,
} from '@invideoio/web-shared/types/Templates';
import { registerAndSendPageViewEvent } from '@invideoio/web-shared/utils/analytics/Register';
import { setCacheControlHeaders } from '@utils/server.utils';
import { processTemplateSearchAPIResponse } from '@utils/template-category/template.utils';
import BannerSlot from '@components/banner-slot';

interface TemplateCategoryPageProps {
  templateList: TemplateSearchResponse;
  filters?: any;
  templatePageData: any;
  templateCategories: any[];
  currentPage: number;
}

function TemplateCategory(props: TemplateCategoryPageProps) {
  const {
    templateList,
    filters,
    templatePageData,
    templateCategories,
    currentPage,
  } = props;

  const globalState = useContext(StateContext);
  const router = useRouter();
  const dispatch = useContext(DispatchContext);
  const { t, lang } = useTranslation('home');
  const { platform, os, browser, config, redirectionLink } = globalState;

  useEffect(() => {
    const AppName = process.env.NEXT_PUBLIC_APP_NAME;

    if (AppName === 'kizoa') {
      AnalyticsUtils.setReferrer(Referrer.KIZOA);
    } else {
      AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    }

    AnalyticsUtils.registerPage(PAGE_TYPE.TEMPLATE_CATEGORY as any, {
      location: window.location.href,
    });

    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);
  }, []);

  useEffect(() => {
    registerAndSendPageViewEvent(PageType.TEMPLATES, {
      location: window.location.href,
    });
  }, []);

  return (
    <AppLayout platform={platform}>
      <HomepageSEO seo={templatePageData.SEO} />
      <BannerSlot browser={browser} />
      <HeaderWithCTA
        templateWorkflowUrl={templatePageData.TemplateSearchFilters}
      />

      <CategoryTemplateHead Head={templatePageData.Head} />

      <div
        className={ivclass(
          'iv-relative iv-overflow-x-hidden',
          'iv-flex md:iv-flex-col sm:iv-flex-col',
          'iv-px-40 md:iv-px-25 sm:iv-px-15',
        )}
      >
        <CategoryTemplateLeftPanel
          templateCategories={templateCategories}
          templateCategoryName={templatePageData.CategoryName}
        />
        <CategoryTemplates
          templateList={templateList.templates}
          filters={filters}
          totalCount={templateList.total_templates}
          currerntPage={currentPage}
          platform={platform}
          moreLink={templatePageData.TemplateSearchFilters}
        />
      </div>
      {templatePageData.TemplateContent && (
        <CategoryTemplateContent
          TemplateContent={templatePageData.TemplateContent}
        />
      )}

      <Footer
        os={os}
        platform={platform}
        socialLinks={globalState.socialLinks}
        exploreLinks={footerExploreLinks[config?.env?.siteId][lang].links}
      />
    </AppLayout>
  );
}

export default TemplateCategory;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const query = context.query;

  if (typeof query.category !== 'string') {
    return {};
  }

  const templatePageData = await fetchTemplatePageData(query.category);

  let page: number | string | string[] | undefined = query.page;

  if (typeof page === 'string') {
    page = parseInt(page);
    if (page < 1) {
      page = 1;
    }
  } else {
    page = 1;
  }

  if (templatePageData && templatePageData.response?.status === 404) {
    return {
      notFound: true,
    };
  }

  const templateCategories = await fetchAllTemplateCategories();

  let templateList: TemplateSearchData | null = null;

  let filters;

  if (templatePageData.TemplateSearchFilters) {
    filters = parseFiltersFromQueryParams(
      templatePageData.TemplateSearchFilters,
    );

    const payload: TemplateSearchPayload = {
      count: TEMPLATE_LIMIT,
      offset: page > 1 ? page * TEMPLATE_LIMIT - TEMPLATE_LIMIT : 0,
      query: filters,
      workflow: 'marketing',
    };

    const templateListResponse = await searchTemplates(payload);

    if (templateListResponse) {
      templateList = processTemplateSearchAPIResponse(templateListResponse);
    }

    if (templateList?.templates.length === 0) {
      return {
        notFound: true,
      };
    }
  }

  let maxage = 3600;

  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    maxage = 300; // 5mins
  }

  setCacheControlHeaders(context, maxage);

  return {
    props: {
      templatePageData,
      templateList,
      filters,
      templateCategories,
      currentPage: page,
    },
  };
}
